// web_components/wcProduct/wcProduct_Renderer.ts

import {
	getPricingDetails,
	getPriorityStatus,
	getProductTypeDetails,
	getQuantityDetails,
	getSpecialLabel,
	getStockStatus,
	wrapCasePattern,
} from './wcProduct_Utils';

/**
 * Generates the HTML for the product based on the provided product data.
 * Handles pricing, discount, stock status, product type, and more.
 *
 * @param {object} dataAttributes - The product data to render.
 * @param {string} instanceId - A unique instance ID for generating unique element IDs.
 * @returns {string} - The HTML string for the product component.
 */
export function renderProductHTML(dataAttributes: any, instanceId: string): string {
	const productData = dataAttributes['variables'];
	const name = wrapCasePattern(productData.product_name);
	const { nowPrice, wasPrice, discount, preorderDeposit } = getPricingDetails(productData);
	const { stockStatusClass, stockStatusMessage } = getStockStatus(productData);
	const { productType, productImage, productUrl } = getProductTypeDetails(productData);
	const specialLabel = getSpecialLabel(productData);
	const { initialQuantity, caseTypeOptions } = getQuantityDetails(productData);
	const {
		stripMessage: priorityStripMessage,
		discount: priorityDiscount,
		stockStatus: priorityStockStatus,
	} = getPriorityStatus(productData, discount, stockStatusMessage);

	// Handle average rating and review count
	const averageRating = productData.average_rating > 0 ? productData.average_rating : 0.0;
	const reviewCount = productData.review_count > 0 ? productData.review_count : 0;

	const qtyInputId = `wc-product__atb-qty-${instanceId}`;
	const qtyErrorId = `wc-product__atb-qty-error-${instanceId}`;
	const selectBtnId = `wc-product__atb-select-btn-${instanceId}`;
	const selectLabelId = `wc-product__atb-select-label-${instanceId}`;

	return `
        <div id="${instanceId}" class="wc-product ${productType} ${stockStatusClass}">
            <div class="wc-product__image-wrapper">
                <span class="wc-product__image-strip-message">${priorityStripMessage || ''}</span>
                ${
					productData.show_discount_badge
						? `
                <span class="wc-product__image-badge-discount">${priorityDiscount > 0 ? `${discount}% off` : ''}</span>`
						: ''
				}
                <span class="wc-product__image-badge-oos">${priorityStockStatus === 'out of stock' ? 'Out of stock' : ''}</span>
                ${productData.is_mystery_case === 'false' && productUrl ? `<a class="wc-product__image-link" href="${productUrl}">` : ''}
					<picture>
						<source srcset="${productImage + '.webp'}" type="image/webp">
						<img class="wc-product__image" src="${productImage + '.png'}" loading="lazy" alt="Product Image" />
					</picture>
				${productData.is_mystery_case === 'false' && productUrl ? `</a>` : ''}

            </div>
            <div class="wc-product__details-wrapper">
                <span class="wc-product__special-label">${specialLabel}</span>
                <h2 class="wc-product__product-name">${name || ''}</h2>
                <div class="wc-product__star-rating" title="${averageRating} out of 5 stars from ${reviewCount} customers">
                	<i class="fa-solid fa-star wc-product__star"></i>
    				<span class="wc-product__rating-text">
						<span class="wc-product__rating-score">${parseFloat(averageRating).toFixed(1)}</span>
						<span class="wc-product__rating-max">/ 5</span>
					</span>
					<span class="wc-product__review-count">(<a href="${productUrl}#accordion-theratings-scrollto" class="wc-product__review-count-total">${reviewCount} reviews</a>)</span>
				</div>
                
                <div class="wc-product__details-facets">
                	${productData.case_size ? `<span class="wc-product__case-size">${productData.case_size} Bottles</span>` : ''}
                	${productData.country ? `<span class="wc-product__country">${productData.country}</span>` : ''}${productData.grape_type_web ? `<span class="wc-product__grape-type">, ${productData.grape_type_web}</span>` : ''}
                    ${productData.vegan ? `<div class="wc-product__vegan"><i class="wc-product__vegan-icon"></i><span>Vegan</span></div>` : ''}
                </div>
                
                ${productData.listing_copy ? `<p class="wc-product__listing-copy">${productData.listing_copy}</p>` : ''}
                
                ${productData.is_mystery_case === 'false' && productUrl ? `<a class="wc-product__more-link" href="${productUrl}">Find out more</a>` : ''}
                
            </div>
            <div class="wc-product__price-wrapper">
            	<span class="wc-product__current-price">£${nowPrice}</span>
				${wasPrice ? `<span class="wc-product__original-price">was £${wasPrice}</span>` : ''}
				${discount > 0 ? `<span class="wc-product__discount">save ${discount}%</span>` : ''}
            </div>
            <form class="wc-product__atb-form">
            	<input type="hidden" name="productId" class="wc-product__product-id" value="${productData.id}">
            	<input type="hidden" name="productType" class="wc-product__product-type" value="${productType}">
            	${
					preorderDeposit
						? `
            		<input type="hidden" name="productDeposit" class="wc-product__product-deposit" value="${preorderDeposit}">`
						: ''
				}
				<div class="wc-product__atb-wrapper">
					<!-- Quantity input field -->
					<label for="${qtyInputId}" class="wc-product__atb-qty-label">Quantity</label>
					<input
					type="text"
					inputmode="numeric"
					pattern="[0-9]*"
					class="wc-product__atb-qty"
					name="wcProductAtbQty"
					id="${qtyInputId}"
					min="1"
					max="99"
					aria-label="Quantity"
					aria-invalid="false"
					aria-describedby="${qtyErrorId}"
					value="${initialQuantity}" />
					
					<!-- Error message element for screen readers and visual feedback -->
					<div id="${qtyErrorId}" class="wc-product__atb-qty-error" aria-live="polite">Message:</div>
					
					<!-- Custom select component -->
					<div class="wc-product__atb-select" name="wcProductAtbSelect">
						<label id="${selectLabelId}" class="wc-product__atb-select-label" for="${selectBtnId}">
							Choose case type
						</label>
						<button
							id="${selectBtnId}"
							class="wc-product__atb-select-btn"
							data-selected-value="Mixed"
							aria-haspopup="listbox"
							aria-expanded="false"
							aria-labelledby="${selectLabelId}">
							Mixed
						</button>
						<ul
							class="wc-product__atb-select-options"
							role="listbox"
							aria-labelledby="${selectBtnId}"
							hidden>
							${caseTypeOptions
								.map(
									(option: string) => `
								<li
									class="wc-product__atb-select-option"
									role="option"
									tabindex="0"
									aria-selected="false"
									data-select-value="${option}">
									${option}
								</li>
								`,
								)
								.join('')}
						</ul>
				</div>
				
				<!-- Add to Basket button -->
				<button type="submit" class="wc-product__atb-btn">
					${stockStatusMessage ? stockStatusMessage : 'Add to basket'}
				</button>
				</div>
			</form>

            ${productData.bundleDiscount ? `<p class="wc-product__bundle-discount">${productData.bundleDiscount}</p>` : ''}
        </div>
    `;
}
